import React from 'react'
import SafeLink from '../../components/common/SafeLink'
import {
  ArrowRight,
  Star,
  BookOpen,
  GraduationCap,
  ClipboardCheck,
  Microscope,
  Brain,
  Sparkles,
  Share2,
  Layers,
  Upload,
  Globe,
  Play,
} from 'lucide-react'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'
import { useAuth } from '../../contexts/AuthContext'
import { motion } from 'framer-motion'
import NewsTicker from '../../components/common/NewsTicker/NewsTicker'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
}
const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 100 } },
}

// Đặc trưng nền tảng tạo khoá học trực tuyến
const features = [
  {
    icon: Upload,
    title: 'Soạn bài đa định dạng',
    desc: 'Tải lên Word, PDF, PowerPoint, video, hình ảnh, HTML tương tác — mỗi tài nguyên là một thẻ nội dung.',
    color: 'from-blue-500 to-indigo-600',
  },
  {
    icon: Layers,
    title: 'Khoá học có cấu trúc',
    desc: 'Sắp xếp bài giảng thành khoá học hoàn chỉnh theo phân phối chương trình (PPCT).',
    color: 'from-violet-500 to-purple-600',
  },
  {
    icon: ClipboardCheck,
    title: 'Thi Online',
    desc: 'Tạo đề thi và bài trắc nghiệm từ ngân hàng câu hỏi đã số hoá, chấm điểm tự động.',
    color: 'from-emerald-500 to-teal-600',
  },
  {
    icon: Share2,
    title: 'Chia sẻ linh hoạt',
    desc: 'Giáo viên chia sẻ khoá học cho đồng nghiệp; admin phân phối nội dung cho toàn trường.',
    color: 'from-amber-500 to-orange-600',
  },
  {
    icon: Microscope,
    title: 'Mô phỏng & Thí nghiệm',
    desc: 'Tích hợp mô phỏng GeoGebra, thí nghiệm ảo và Lab IoT trực tiếp trong bài giảng.',
    color: 'from-cyan-500 to-blue-600',
  },
  {
    icon: Brain,
    title: 'AI hỗ trợ dạy học',
    desc: 'AI gia sư, sinh câu hỏi và gợi ý nội dung giúp soạn giảng nhanh và cá nhân hoá.',
    color: 'from-pink-500 to-rose-600',
  },
]

// Lối tắt tới các khu vực chính (giữ Mô phỏng, Nhà bác học, Flashcard)
const quickLinks = [
  { to: '/teacher/khoa-hoc', label: 'Khoá học', icon: BookOpen, color: 'bg-blue-50 text-blue-600' },
  { to: '/trac-nghiem', label: 'Thi Online', icon: ClipboardCheck, color: 'bg-emerald-50 text-emerald-600' },
  { to: '/mo-phong', label: 'Mô phỏng', icon: Microscope, color: 'bg-cyan-50 text-cyan-600' },
  { to: '/nha-bac-hoc', label: 'Nhà bác học', icon: GraduationCap, color: 'bg-amber-50 text-amber-600' },
  { to: '/flashcard', label: 'Flashcard', icon: Layers, color: 'bg-violet-50 text-violet-600' },
  { to: '/ai-hoi-dap', label: 'AI Gia sư', icon: Brain, color: 'bg-pink-50 text-pink-600' },
]

const TrangChu = () => {
  const { isAuthenticated } = useAuth()

  return (
    <div className="min-h-screen bg-transparent">
      <Navigation />

      <motion.div initial="hidden" animate="visible" variants={containerVariants} className="space-y-16 md:space-y-20">
        {/* Hero — full màn hình, nội dung căn giữa, có chỉ báo cuộn */}
        <section className="relative overflow-hidden min-h-[calc(100vh-4rem)] flex items-center">
          <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12 text-center">
            {/* Badge giải thưởng — quầng sáng thở nhẹ + vệt sáng lướt qua */}
            <motion.div
              variants={itemVariants}
              className="relative inline-flex items-center px-5 py-2 bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 text-sm font-bold rounded-full mb-8 border border-amber-300 overflow-hidden"
            >
              <motion.span
                aria-hidden="true"
                className="absolute inset-0 rounded-full pointer-events-none"
                animate={{ boxShadow: ['0 0 0 0 rgba(245,158,11,0)', '0 0 22px 5px rgba(245,158,11,0.38)', '0 0 0 0 rgba(245,158,11,0)'] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.span
                aria-hidden="true"
                className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/80 to-transparent pointer-events-none"
                initial={{ x: '-150%' }}
                animate={{ x: '380%' }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 2.6, ease: 'easeInOut' }}
              />
              <Star className="relative w-4 h-4 mr-2 text-amber-500 fill-amber-400" />
              <span className="relative">🏆 Giải Nhất — Giải thưởng tiên phong ứng dụng AI trong giáo dục Việt Nam 2025</span>
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="text-4xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight tracking-tight"
            >
              Nền tảng Dạy và học
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Vật lí thông minh
              </span>
              <span className="block mt-4 text-xl md:text-2xl font-semibold text-slate-600">
                đáp ứng nhu cầu dạy học STEM, AI và chuyển đổi số
              </span>
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-xl md:text-2xl text-slate-600 mb-10 max-w-3xl mx-auto leading-relaxed font-light"
            >
              Giáo viên tự tạo bài giảng đa phương tiện, dựng khoá học hoàn chỉnh và tổ chức thi online.
              Học sinh học mọi lúc, mọi nơi.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
              <SafeLink
                to="/teacher/khoa-hoc"
                className="btn-primary group inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl hover:text-white/90 ring-4 ring-blue-500/20"
              >
                <BookOpen className="mr-2 w-5 h-5" />
                Khám phá khoá học
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </SafeLink>
              <SafeLink
                to="/teacher/elearning"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-2xl bg-white text-slate-800 border border-slate-200 shadow-lg hover:shadow-xl hover:bg-slate-50 transition-all duration-300 transform hover:scale-105"
              >
                <Sparkles className="mr-2 w-5 h-5 text-purple-500" />
                {isAuthenticated ? 'Soạn bài giảng' : 'Bắt đầu tạo'}
              </SafeLink>
            </motion.div>

            {/* Bản nâng cấp đặc biệt từ sản phẩm đạt Giải Nhất */}
            <motion.div variants={itemVariants} className="mt-12 max-w-4xl mx-auto">
              <div className="rounded-3xl border-2 border-amber-200 bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 shadow-lg px-6 py-6 md:px-10 text-left">
                <p className="text-center text-sm font-bold uppercase tracking-wide text-amber-700 mb-4">
                  ✨ Bản nâng cấp đặc biệt từ sản phẩm đạt Giải Nhất
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <SafeLink
                    to="/teacher/elearning"
                    className="group flex gap-3 p-4 rounded-2xl bg-white/80 border border-amber-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all no-underline"
                  >
                    <span className="text-2xl">🤖</span>
                    <span>
                      <span className="block font-bold text-slate-800 group-hover:text-amber-700">Soạn bài giảng bằng AI</span>
                      <span className="block text-sm text-slate-500 mt-0.5">
                        Cấu trúc chuẩn sư phạm 4 hoạt động, câu hỏi tương tác chấm từng câu, mô phỏng &amp; video tự gắn theo bài.
                      </span>
                    </span>
                  </SafeLink>
                  <SafeLink
                    to="/teacher/khoa-hoc"
                    className="group flex gap-3 p-4 rounded-2xl bg-white/80 border border-amber-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all no-underline"
                  >
                    <span className="text-2xl">🎓</span>
                    <span>
                      <span className="block font-bold text-slate-800 group-hover:text-amber-700">Tạo khoá học hoàn chỉnh</span>
                      <span className="block text-sm text-slate-500 mt-0.5">
                        Ghi danh học sinh, theo dõi tiến độ từng phần, thống kê tương tác chi tiết cho giáo viên.
                      </span>
                    </span>
                  </SafeLink>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Chỉ báo cuộn xuống — tinh tế, không màu mè */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="absolute bottom-5 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-1.5"
            aria-hidden="true"
          >
            <span className="text-[11px] font-medium text-slate-400 tracking-widest uppercase">Cuộn xuống</span>
            <div className="w-6 h-10 rounded-full border-2 border-slate-300/80 flex items-start justify-center p-1.5">
              <div className="w-1.5 h-2.5 rounded-full bg-slate-400 animate-bounce" />
            </div>
          </motion.div>
        </section>

        {/* Lối tắt nhanh */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
          >
            {quickLinks.map((q) => {
              const Icon = q.icon
              return (
                <SafeLink
                  key={q.to}
                  to={q.to}
                  className="flex flex-col items-center gap-3 p-6 bg-white/70 backdrop-blur-md rounded-2xl border border-white/50 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all no-underline"
                >
                  <span className={`w-12 h-12 rounded-xl flex items-center justify-center ${q.color}`}>
                    <Icon className="w-6 h-6" />
                  </span>
                  <span className="text-sm font-bold text-slate-700">{q.label}</span>
                </SafeLink>
              )
            })}
          </motion.div>
        </section>

        {/* Đặc trưng nền tảng */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }} className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">Tất cả công cụ để dạy học trực tuyến</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto font-light">
              Từ soạn bài, dựng khoá học đến tổ chức thi — trong một nền tảng duy nhất
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon
              return (
                <motion.div
                  key={i}
                  variants={itemVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.25 }}
                  whileHover={{ y: -6 }}
                  className="bg-white/70 backdrop-blur-md rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all border border-white/50"
                >
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${f.color} text-white mb-5 shadow-lg`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              )
            })}
          </div>
        </section>

        {/* Physics English */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-cyan-500 to-blue-600 p-8 md:p-12 shadow-2xl"
          >
            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl" />
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left text-white">
                <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-sm font-medium mb-4 border border-white/30">
                  <Globe className="w-4 h-4 mr-2" />
                  <span>Song ngữ Anh - Việt</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Tiếng Anh chuyên ngành Vật lí</h2>
                <p className="text-lg text-cyan-50 max-w-xl leading-relaxed">
                  Học thuật ngữ chuyên ngành với phát âm chuẩn từ AI, nâng cao khả năng đọc hiểu tài liệu quốc tế.
                </p>
              </div>
              <a
                href="https://eng.vatli365.vn"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center px-8 py-4 bg-white text-blue-600 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:bg-blue-50"
              >
                <span>Khám phá ngay</span>
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </motion.div>
        </section>

        {/* Flashcard — đặt sau Tiếng Anh Vật lí */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-500 to-purple-600 p-8 md:p-12 shadow-2xl"
          >
            <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl" />
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left text-white">
                <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-sm font-medium mb-4 border border-white/30">
                  <Layers className="w-4 h-4 mr-2" />
                  <span>Ghi nhớ nhanh công thức &amp; khái niệm</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Flashcard Vật lí</h2>
                <p className="text-lg text-violet-50 max-w-xl leading-relaxed">
                  Ôn tập công thức, định nghĩa và hằng số quan trọng bằng thẻ ghi nhớ lật tương tác theo từng bài học.
                </p>
              </div>
              <SafeLink
                to="/flashcard"
                className="flex-shrink-0 inline-flex items-center px-8 py-4 bg-white text-purple-600 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:bg-purple-50 no-underline"
              >
                <span>Luyện Flashcard</span>
                <ArrowRight className="ml-2 w-5 h-5" />
              </SafeLink>
            </div>
          </motion.div>
        </section>

        {/* CTA cuối */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <motion.div
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-3xl p-12 text-center text-white shadow-2xl relative overflow-hidden"
          >
            <div className="relative z-10">
              <h2 className="text-4xl font-bold mb-6 text-white">Sẵn sàng tạo khoá học của bạn?</h2>
              <p className="text-xl mb-10 opacity-90 max-w-2xl mx-auto font-light text-indigo-100">
                Bắt đầu soạn bài giảng đa phương tiện và dựng khoá học trực tuyến chỉ trong vài phút
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <SafeLink
                  to="/teacher/khoa-hoc"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-900 hover:bg-gray-50 font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Vào khu vực khoá học
                  <ArrowRight className="ml-2 w-5 h-5" />
                </SafeLink>
                <SafeLink
                  to="/mo-phong"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 backdrop-blur-sm border border-white/20"
                >
                  Khám phá mô phỏng
                  <Play className="ml-2 w-5 h-5" />
                </SafeLink>
              </div>
            </div>
          </motion.div>
        </section>
      </motion.div>

      <NewsTicker />
      <Footer />
    </div>
  )
}

export default TrangChu
